/**
 * Persistent Logger - 将日志保存到 localStorage
 * 用于调试页面跳转后丢失的控制台日志
 */

interface LogEntry {
  timestamp: string;
  level: 'log' | 'warn' | 'error' | 'info';
  category: string;
  message: string;
  data?: any;
}

const MAX_LOGS = 200; // 最多保存 200 条日志
const STORAGE_KEY = 'debug_logs';

class PersistentLogger {
  private logs: LogEntry[] = [];

  constructor() {
    this.loadLogs();
  }

  private loadLogs() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load logs from localStorage');
    }
  }

  private saveLogs() {
    try {
      // 只保留最新的 MAX_LOGS 条
      if (this.logs.length > MAX_LOGS) {
        this.logs = this.logs.slice(-MAX_LOGS);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.logs));
    } catch (e) {
      console.error('Failed to save logs to localStorage');
    }
  }

  private addLog(level: LogEntry['level'], category: string, message: string, data?: any) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data: data !== undefined ? this.serializeData(data) : undefined,
    };

    this.logs.push(entry);
    this.saveLogs();

    // 同时输出到控制台
    const consoleMethod = console[level] || console.log;
    const prefix = `[${entry.timestamp}] [${category}]`;
    if (data !== undefined) {
      consoleMethod(prefix, message, data);
    } else {
      consoleMethod(prefix, message);
    }
  }

  private serializeData(data: any): any {
    try {
      // 尝试序列化，如果失败则转为字符串
      JSON.stringify(data);
      return data;
    } catch (e) {
      return String(data);
    }
  }

  log(category: string, message: string, data?: any) {
    this.addLog('log', category, message, data);
  }

  info(category: string, message: string, data?: any) {
    this.addLog('info', category, message, data);
  }

  warn(category: string, message: string, data?: any) {
    this.addLog('warn', category, message, data);
  }

  error(category: string, message: string, data?: any) {
    this.addLog('error', category, message, data);
  }

  /**
   * 获取所有日志
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * 获取格式化的日志文本
   */
  getLogsText(): string {
    return this.logs
      .map((entry) => {
        const time = new Date(entry.timestamp).toLocaleTimeString();
        let line = `[${time}] [${entry.level.toUpperCase()}] [${entry.category}] ${entry.message}`;
        if (entry.data !== undefined) {
          line += `\n  Data: ${JSON.stringify(entry.data, null, 2)}`;
        }
        return line;
      })
      .join('\n');
  }

  /**
   * 清除所有日志
   */
  clear() {
    this.logs = [];
    localStorage.removeItem(STORAGE_KEY);
    console.log('[PersistentLogger] Logs cleared');
  }

  /**
   * 导出日志为文本文件
   */
  exportLogs(): string {
    return this.getLogsText();
  }

  /**
   * 打印所有日志到控制台
   */
  printLogs() {
    console.log('=== PERSISTENT LOGS ===');
    console.log(this.getLogsText());
    console.log('=== END LOGS ===');
  }

  /**
   * 获取最近的 N 条日志
   */
  getRecentLogs(count: number): LogEntry[] {
    return this.logs.slice(-count);
  }

  /**
   * 按类别过滤日志
   */
  getLogsByCategory(category: string): LogEntry[] {
    return this.logs.filter((log) => log.category === category);
  }

  /**
   * 按级别过滤日志
   */
  getLogsByLevel(level: LogEntry['level']): LogEntry[] {
    return this.logs.filter((log) => log.level === level);
  }
}

// 导出单例
export const pLogger = new PersistentLogger();

// 在全局对象上暴露，方便在控制台调试
if (typeof window !== 'undefined') {
  (window as any).debugLogs = {
    print: () => pLogger.printLogs(),
    clear: () => pLogger.clear(),
    export: () => pLogger.exportLogs(),
    get: () => pLogger.getLogs(),
    recent: (count = 20) => pLogger.getRecentLogs(count),
  };

  console.log('💡 Debug logs available via window.debugLogs');
  console.log('   window.debugLogs.print()   - print all logs');
  console.log('   window.debugLogs.recent()  - show recent logs');
  console.log('   window.debugLogs.clear()   - clear logs');
  console.log('   window.debugLogs.export()  - export logs as text');
}
